// src/components/Stocks/Listing/StockListingTraitTable.tsx
import { Daum } from '../types/TCategory'
import TraitTable from '@/stories/components/DetailPage/TraitTable'
import { TraitTypes } from '@/components/MutualFunds/Detail/MFDetailTypes'
import SortIcon from '@/images/sort.svg'
import SortDownIcon from '@/images/sort-down.svg'
import rupeeCoversion from '@/root/utils/common/numbers/rupee-conversion'
import { RUPEE_SYMBOL } from '@/root/constants'
import { truncateDecimal } from '@/root/utils/common/numbers/truncate-number'
import {
  generateDetailUrl,
  SORT_KEY_MAP,
  SortKeys,
  SortKeyType,
} from '@/components/Stocks/Listing/constants'

export const sortIcon = (asc?: boolean) => (
  <SortDownIcon
    className={`text-brand-blue-azureRadiance ${asc ? 'rotate-180' : ''}`}
    height={16}
    width={16}
  />
)

type StockItem = Pick<
  Daum,
  | 'name'
  | 'price'
  | 'change'
  | 'volume'
  | 'mcap'
  | 'logo'
  | 'navlinks'
  | 'relativepath'
  | 'ind_key'
> & {
  custom_column_value?: string
  applicable_mtf_margin?: string
  historic_returns?: {
    '1w'?: number
    '1m'?: number
    '3m'?: number
    '6m'?: number
    '1y'?: number
    '3y'?: number
    '5y'?: number
  }
}

interface StocksListingTraitTableProps {
  data?: StockItem[]
  activeSortKey?: string
  activeSortOrder?: string
  onFilterUpdate?: (params: {
    type: string
    key: string | number
    value: string
    selectionKey: string
  }) => void
  sortKeys?: typeof SortKeys
  includeBoostColumn?: boolean
  custom_column_key?: string
  headingApplicableMTF: string
}

type ColumnType = {
  title?: string | number
  trait?: 'button' | 'text' | 'actionButton' | 'graph'
  textColor?: string
  image_url?: string
  image?: { className: string; height: number; width: number }
  style?: string
  className?: string
}

// Formatters
const formatChange = (change: number) => {
  if (change === undefined || change === null) return '-'
  const isPositive = change >= 0
  const arrow = isPositive ? '▲' : '▼'
  return `${arrow} ${Math.abs(parseFloat(change.toFixed(2)))}%`
}

const formatPrice = (price: number) =>
  price ? `${RUPEE_SYMBOL}${price.toFixed(2)}` : '0'

const formatMarketCap = (mcap: number) => (mcap ? rupeeCoversion(mcap) : '0')

const formatHistoricReturn = (value?: number) => {
  if (value === undefined || value === null) return '0%'
  const arrow = value > 0 ? '▲' : '▼'
  return `${arrow} ${Math.abs(value).toFixed(2)}%`
}

const calculateBoost = (margin?: string) => {
  if (!margin) return null
  const val = parseFloat(margin)
  return val ? 100 / val : null
}

const getReturnClassName = (value?: number) =>
  value !== undefined && value >= 0
    ? 'text-brand-green-dark'
    : 'text-brand-red-cinnabar'

// Custom column helpers
const formatCustomValue = (
  custom_value?: string,
  custom_column_key?: string,
) => {
  if (!custom_value || !custom_column_key) return null
  const val = parseFloat(custom_value)
  const arrow = val >= 0 ? '▲' : '▼'

  const returnTypes = ['1y_return', '3y_return', '1m_return', '1w_return']
  if (returnTypes.includes(custom_column_key)) {
    return `${arrow} ${Math.abs(val).toFixed(2)}%`
  }
  if (custom_column_key === 'div_yield') return `${custom_value}%`
  return custom_value
}

const getCustomClassName = (
  custom_value?: string,
  custom_column_key?: string,
) => {
  if (!custom_value || !custom_column_key) return ''
  if (custom_column_key === 'analyst_rating') {
    if (custom_value === 'SELL') return 'text-brand-red-cinnabar'
    if (custom_value === 'HOLD') return 'text-brand-yellow-hold'
    return 'text-brand-green-dark'
  }
  const returnTypes = ['1y_return', '3y_return', '1m_return', '1w_return']
  if (returnTypes.includes(custom_column_key)) {
    return parseFloat(custom_value) >= 0
      ? 'text-brand-green-dark'
      : 'text-brand-red-cinnabar'
  }
  return ''
}

const getCustomHeading = (custom_column_key?: string) => {
  const headings: Record<string, string> = {
    dma: 'DMA',
    rsi_14: 'RSI 14',
    analyst_rating: 'Analyst Rating',
    div_yield: 'Dividend Yield',
    '1y_return': '1Y Return',
    '3y_return': '3Y Return',
    '1m_return': '1M Return',
    '1w_return': '1W Return',
  }
  return headings[custom_column_key ?? ''] || 'Market Cap'
}

// Build table columns for a stock item
const buildColumns = (
  item: StockItem,
  includeBoostColumn: boolean,
  custom_column_key?: string,
): ColumnType[] => {
  const boost = calculateBoost(item.applicable_mtf_margin)
  const columns: ColumnType[] = [
    {
      title: item.name,
      trait: 'text',

      style: 'bold',
    },
  ]

  if (includeBoostColumn) {
    columns.push({
      title: boost ? `${truncateDecimal(boost, 1)}x` : '-',
      trait: 'text',
      style: 'bold',
    })
  }

  // Price
  columns.push({
    title: formatPrice(item.price),
    trait: 'text',
    style: 'bold',
  })

  // 1D Change
  columns.push({
    title: formatChange(item.change),
    trait: 'text',
    className: getReturnClassName(item.change),
    style: 'bold',
  })

  // Historic returns: 1Y, 3Y, 5Y
  const returns = ['1y', '3y', '5y'] as const
  returns.forEach(key => {
    const value = item.historic_returns?.[key]
    columns.push({
      title: formatHistoricReturn(value),
      trait: 'text',
      className: getReturnClassName(value),
      style: 'bold',
    })
  })

  // Volume
  columns.push({
    title: rupeeCoversion(item.volume),
    trait: 'text',
    style: 'bold',
  })

  // Market Cap or Custom Column
  if (custom_column_key && item.custom_column_value) {
    columns.push({
      title:
        formatCustomValue(item.custom_column_value, custom_column_key) ?? '-',
      trait: 'text',
      className: getCustomClassName(
        item.custom_column_value,
        custom_column_key,
      ),
      style: 'bold',
    })
  } else {
    columns.push({
      title: formatMarketCap(item.mcap),
      trait: 'text',
      style: 'bold',
    })
  }

  return columns
}

const StocksListingTraitTable = ({
  data = [],
  activeSortKey = 'mCap',
  activeSortOrder = 'desc',
  onFilterUpdate,
  sortKeys = SortKeys,
  includeBoostColumn = false,
  custom_column_key,
  headingApplicableMTF,
}: StocksListingTraitTableProps) => {
  const getSortKey = (key: SortKeyType): string => SORT_KEY_MAP[key]
  const getSortConfig = (sortKey: string) =>
    sortKeys?.find(({ key }) => key === sortKey)

  const handleSort = (sortKey: SortKeyType) => {
    if (!onFilterUpdate) return
    const mappedSortKey = getSortKey(sortKey)
    const isCurrentlyActive = activeSortKey === mappedSortKey
    const sortConfig = getSortConfig(mappedSortKey)

    if (isCurrentlyActive && sortConfig?.subList) {
      const currentIndex = sortConfig.subList.findIndex(
        ({ key }) => key === activeSortOrder,
      )
      const newOrder = sortConfig.subList[currentIndex === 0 ? 1 : 0]?.key
      if (newOrder) {
        onFilterUpdate({
          type: 'sortOrder',
          key: newOrder,
          value: '',
          selectionKey: '',
        })
      }
    } else {
      onFilterUpdate({
        type: 'sortKey',
        key: mappedSortKey,
        value: '',
        selectionKey: '',
      })
    }
  }

  const getActionButton = (
    title: string,
    sortKey: SortKeyType,
    customClassName?: string,
    tooltipTitle?: string,
  ) => {
    const mappedSortKey = getSortKey(sortKey)
    const isActive = activeSortKey === mappedSortKey
    const sortConfig = getSortConfig(mappedSortKey)
    const isAsc =
      isActive &&
      sortConfig?.subList &&
      sortConfig.subList.findIndex(({ key }) => key === activeSortOrder) === 1

    return {
      title,
      trait: 'actionButton' as TraitTypes['trait'],
      className: `${
        isActive ? 'text-brand-blue-azureRadiance font-medium' : ''
      } ${customClassName ?? ''}`.trim(),
      children: isActive ? (
        sortIcon(isAsc)
      ) : (
        <SortIcon height={16} width={16} />
      ),
      onClick: () => handleSort(sortKey),
      tooltipTitle,
    }
  }

  const tableHeading = [
    getActionButton('Name', SortKeyType.NAME, '!justify-start'),
    ...(includeBoostColumn
      ? [{ title: headingApplicableMTF, trait: 'text' as const }]
      : []),
    getActionButton('Price', SortKeyType.PRICE),
    getActionButton('1D', SortKeyType.CHANGE),
    getActionButton('1Y', SortKeyType.RETURN_1Y),
    getActionButton('3Y', SortKeyType.RETURN_3Y),
    getActionButton('5Y', SortKeyType.RETURN_5Y),
    getActionButton('Volume', SortKeyType.VOLUME),
    custom_column_key
      ? { title: getCustomHeading(custom_column_key), trait: 'text' as const }
      : getActionButton(
          'MCap',
          SortKeyType.MCAP,
          undefined,
          'Market Capitalization',
        ),
  ]

  return (
    <TraitTable
      traitTable={{
        tableContent: data?.filter(Boolean)?.map(item => ({
          columns: buildColumns(item, includeBoostColumn, custom_column_key),
          url: generateDetailUrl({
            stock_name: item.name,
            company_code: item.ind_key ?? '',
            link: item.navlinks?.web || item.relativepath,
          }),
        })),
        tableHeading,
      }}
    />
  )
}

export default StocksListingTraitTable
